import React, { useState } from 'react';
import Fade from 'react-reveal/Fade';
import Slide from 'react-reveal/Slide';
import { Link } from 'atomic-router-react';
import { useUnit } from 'effector-react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { useT } from 'src/i18n';
import { $locale, localeRoutes } from 'src/models/locale';
import { Header } from 'src/ui/components/Components';
import { Seo } from 'src/ui/components/Seo';

import './Projects.scss';

const projectsMeta = [
  {
    id: 'pypy-ats',
    tags: ['Python', 'Django', 'PostgreSQL', 'Docker', 'Telegram Bot'],
    screenshots: [
      { id: 1, src: '/images/projects/pypy/LP.png' },
      { id: 2 },
      { id: 3 },
      { id: 4 },
      { id: 5, src: '/images/projects/pypy/analitic.png' },
      { id: 6 },
    ],
  },
  {
    id: 'fants',
    tags: ['Python', 'Django', 'PostgreSQL', 'Docker', 'Sentry', 'Prometheus'],
    screenshots: [
      { id: 1, src: '/images/projects/fants/home.png' },
      { id: 2, src: '/images/projects/fants/games.png' },
      { id: 3, src: '/images/projects/fants/test.png' },
      { id: 4 },
      { id: 5, src: '/images/projects/fants/blog.png' },
      { id: 6 },
    ],
  },
  {
    id: 'royal-shine',
    tags: ['Python', 'Django', 'PostgreSQL', 'Docker', 'Telegram Bot'],
    screenshots: [
      { id: 1, src: '/images/projects/rs/rs_lp.png' },
      { id: 2, src: '/images/projects/rs/booking.png' },
      { id: 3 },
      { id: 4 },
      { id: 5, src: '/images/projects/rs/price.png' },
      { id: 6, src: '/images/projects/rs/portfolio.png' },
    ],
  },
  {
    id: 'mewcraft',
    tags: ['C++', 'Qt 6', 'Python', 'Django', 'DRF', 'PostgreSQL', 'Redis', 'Docker', 'nginx'],
    screenshots: [
      { id: 1, src: '/images/projects/mewCraft/launcher.png' },
      { id: 2, src: '/images/projects/mewCraft/launcher_servers.png' },
      { id: 3, src: '/images/projects/mewCraft/LauncherDownload.png' },
      { id: 4, src: '/images/projects/mewCraft/homePage.png' },
      { id: 5, src: '/images/projects/mewCraft/LC.png' },
      { id: 6, src: '/images/projects/mewCraft/wiki.png' },
    ],
  },
  {
    id: 'fincollect',
    tags: ['PHP', 'WordPress'],
    screenshots: [
      { id: 1 },
      { id: 2 },
      { id: 3 },
      { id: 4 },
    ],
  },
  {
    id: 'jeetdonate',
    tags: ['Python', 'WebSocket', 'OBS'],
    screenshots: [
      { id: 1 },
      { id: 2 },
      { id: 3 },
      { id: 4 },
    ],
  },
  {
    id: 'jeetbot',
    tags: ['Next.js', 'WebSocket', 'TTS', 'Twitch API'],
    screenshots: [
      { id: 1 },
      { id: 2 },
      { id: 3 },
      { id: 4 },
    ],
  },
];

export const Projects = () => {
  const t = useT();
  const locale = useUnit($locale);
  const homeRouteForLocale = localeRoutes[locale].home;

  return (
    <>
      <Seo page="projects" />
      <Header minimal />

      <Fade>
        <div className="projects-page">
          <h1 className="heading heading_projects">
            {t.projectsPage.heading}
          </h1>
          <p className="paragraph paragraph_projects">
            {t.projectsPage.subline}
          </p>
        </div>
      </Fade>

      <div className="projects-list">
        {projectsMeta.map((meta) => (
          <ProjectCard
            key={meta.id}
            meta={meta}
            copy={t.projectsPage.projects[meta.id]}
          />
        ))}
      </div>

      <Fade>
        <div className="projects-back">
          <Link to={homeRouteForLocale} className="projects-back__link">
            {t.projectsPage.back}
          </Link>
        </div>
      </Fade>
    </>
  );
};

const ProjectCard = ({ meta, copy }) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const screenshots = meta.screenshots.map((screenshot, index) => ({
    ...screenshot,
    alt: copy.screenshotAlts[index],
  }));

  const slides = screenshots
    .filter((s) => s.src)
    .map((s) => ({ src: s.src, alt: s.alt }));

  const handleScreenshotClick = (screenshot) => {
    const idx = slides.findIndex((s) => s.src === screenshot.src);
    if (idx !== -1) setLightboxIndex(idx);
  };

  return (
    <Slide bottom>
      <div className="project-card">
        <div className="project-card__header">
          <div className="project-card__title-block">
            <h2 className="project-card__title">{copy.title}</h2>
            <span className="project-card__subtitle">{copy.subtitle}</span>
          </div>
          <div className="project-card__tags">
            {meta.tags.map((tag) => (
              <span key={tag} className="project-card__tag">{tag}</span>
            ))}
          </div>
        </div>

        <p className="project-card__description">{copy.description}</p>

        {copy.features && (
          <div className="project-card__features">
            {copy.features.map((group) => (
              <div key={group.title} className="feature-group">
                <h3 className="feature-group__title">{group.title}</h3>
                <ul className="feature-group__list">
                  {group.items.map((item) => (
                    <li key={item} className="feature-group__item">{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className="project-card__screenshots">
          {screenshots.map((screenshot) => (
            <div key={screenshot.id} className="project-card__screenshot">
              {screenshot.src ? (
                <img
                  className="project-card__screenshot-img"
                  src={screenshot.src}
                  alt={screenshot.alt}
                  loading="lazy"
                  onClick={() => handleScreenshotClick(screenshot)}
                />
              ) : (
                <div className="project-card__screenshot-placeholder">
                  <span className="project-card__screenshot-icon">🖼</span>
                  <span className="project-card__screenshot-text">{screenshot.alt}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <Lightbox
          open={lightboxIndex >= 0}
          index={lightboxIndex}
          close={() => setLightboxIndex(-1)}
          slides={slides}
        />
      </div>
    </Slide>
  );
};
